import { Controller, useFormContext } from "react-hook-form";
import { Form, TextArea } from "semantic-ui-react";

import type { ArrayPath, FieldArray, FieldValues, Path } from "react-hook-form";
import type { FormFieldProps, TextAreaProps } from "semantic-ui-react";

type FormArrayTextAreaProps<T extends FieldValues, A extends ArrayPath<T> = ArrayPath<T>> = {
  fieldArrayName: A;
  index: number;
  fieldKey: FieldArray<T, A> extends Record<string, unknown> ? Extract<keyof FieldArray<T, A>, string> : string;
  textAreaProps?: TextAreaProps;
  formFieldProps?: FormFieldProps;
};

/**
 * The casting as Path<T> on the constructed path is kinda unavoidable
 * TS can't infer that a template literal built from runtime values at `ArrayPath<T>.{ArrayPath<T>}.ArrayPath<T>.{number}.${string}` satisfies `Path<T>`.
 * But what we can do is check for inputs to be fully type-safe:
 * fieldArrayName is constrained to ArrayPath<T>, so it only accepts fields that are actually arrays in our form schema.
 * fieldKey is constrained to the actual string keys of the array's element type via FieldArray<T, A>, so we still get autocomplete and compile-time errors for invalid keys.
 */
export const FormArrayTextArea = <T extends FieldValues>({ fieldArrayName, formFieldProps, textAreaProps, index, fieldKey }: FormArrayTextAreaProps<T>) => {
  const methods = useFormContext<T>();
  const path = `${fieldArrayName}.${index}.${fieldKey}` as Path<T>;

  return (
    <Controller
      name={path}
      control={methods.control}
      render={({ field, fieldState: { invalid, isTouched } }) => (
        <Form.Field {...formFieldProps} error={invalid && isTouched}>
          <TextArea {...methods.register(path)} {...field} {...textAreaProps} />
        </Form.Field>
      )}
    />
  );
};
